class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._isRendering = false;
    this._pendingRender = null;
    this._freeGiftDebounceTimer = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Custom delegated event listeners for shipping protection toggle
    this.addEventListener('change', (event) => {
      if (event.target.id === 'CartShippingProtectionToggle') {
        const variantId = event.target.getAttribute('data-variant-id');
        const quantity = event.target.checked ? 1 : 0;

        const url = window.Shopify.routes.root + 'cart/update.js';
        const body = JSON.stringify({
          updates: { [variantId]: quantity },
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        });

        this._internalFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: body
        })
        .then(response => response.json())
        .then(response => {
          if (response.status && response.status !== 200) {
            console.error('Cart Error:', response);
            return;
          }
          this.renderContents(response);
        })
        .catch((e) => {
          console.error(e);
        });
      }
    });

    // Cross-sell add button handler
    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('cross-sell-add-btn')) {
        const variantId = event.target.getAttribute('data-variant-id');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        this._internalFetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData
        })
        .then(response => response.json())
        .then(response => {
          if (response.status && response.status !== 200) {
            console.error('Cart Error:', response);
            return;
          }
          this._internalFetch(window.Shopify.routes.root + 'cart.json')
            .then(res => res.json())
            .then(cart => {
              response.item_count = cart.item_count;
              this.renderContents(response);
            });
        })
        .catch((error) => {
          console.error('Error:', error);
        });
      }
    });

    // Subscribe to cart updates - debounced free gift check
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.debouncedCheckFreeGift();
    });

    // Listen for cart:refresh event (from global.js custom product handler)
    document.documentElement.addEventListener('cart:refresh', () => {
      this.refreshCartDrawer();
    });
  }

  // Internal fetch that bypasses the monkey-patched global fetch to prevent re-triggering handleCartChange
  _internalFetch(url, options) {
    const fetchFn = window._originalFetch || window.fetch;
    return fetchFn.call(window, url, options);
  }

  // Debounced free gift check to prevent cascading calls
  debouncedCheckFreeGift() {
    clearTimeout(this._freeGiftDebounceTimer);
    this._freeGiftDebounceTimer = setTimeout(() => {
      this.checkFreeGift();
    }, 400);
  }

  // Dedicated refresh method that fetches fresh section HTML
  refreshCartDrawer() {
    if (this._isRendering) return;

    this._internalFetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: {}, sections: 'cart-drawer,cart-icon-bubble' })
    })
    .then(res => res.json())
    .then(response => {
      if (response.sections) {
        this.renderContents(response);
      }
    })
    .catch(e => console.error('Cart refresh error:', e));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Prevent re-entrant rendering
    if (this._isRendering) {
      this._pendingRender = parsedState;
      return;
    }
    this._isRendering = true;

    try {
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.classList.remove('is-empty');
      }

      if (parsedState && parsedState.item_count > 0) {
        this.classList.remove('is-empty');
      }

      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      // Re-bind overlay click after innerHTML replacement
      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', this.close.bind(this));
        }
        // Only open if not already active (don't re-trigger animations on quantity updates)
        if (!this.classList.contains('active')) {
          this.open();
        }
      });

      // Reinit swiper after render
      if (typeof initCartSwiper === 'function') {
        setTimeout(initCartSwiper, 50);
      } else if (typeof Swiper !== 'undefined') {
        setTimeout(() => {
          const swiperEl = this.querySelector('.cart-cross-sell-swiper');
          if (swiperEl) {
            new Swiper('.cart-cross-sell-swiper', {
              slidesPerView: 'auto',
              spaceBetween: 10,
              freeMode: true,
              grabCursor: true
            });
          }
        }, 50);
      }
    } finally {
      this._isRendering = false;
    }

    // Check free gift AFTER rendering is done (not re-entrantly)
    this.debouncedCheckFreeGift();

    // If another render was queued while we were rendering, process it
    if (this._pendingRender) {
      const pending = this._pendingRender;
      this._pendingRender = null;
      requestAnimationFrame(() => this.renderContents(pending));
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  checkFreeGift() {
    if (window.freeGiftAdding) return;
    const freeGiftData = document.getElementById('FreeGiftData');
    if (!freeGiftData) return;
    const addJson = freeGiftData.getAttribute('data-items-to-add');
    const updateJson = freeGiftData.getAttribute('data-items-to-update');

    let itemsToAdd = [];
    let itemsToUpdate = {};

    try { if (addJson) itemsToAdd = JSON.parse(addJson); } catch (e) { console.error('Error parsing addJson:', e); }
    try { if (updateJson) itemsToUpdate = JSON.parse(updateJson); } catch (e) { console.error('Error parsing updateJson:', e); }

    // Nothing to do
    if (itemsToAdd.length === 0 && Object.keys(itemsToUpdate).length === 0) return;

    window.freeGiftAdding = true;

    const updateCart = async () => {
      try {
        if (itemsToAdd.length > 0) {
          await this._internalFetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });
        }
        if (Object.keys(itemsToUpdate).length > 0) {
          await this._internalFetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: itemsToUpdate })
          });
        }
        // Fetch fresh sections for re-render
        const response = await this._internalFetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: {}, sections: 'cart-drawer,cart-icon-bubble' })
        }).then(res => res.json());

        if (response && response.sections) {
          this.renderContents(response);
        }
      } catch (e) {
        console.error('Free gift update error:', e);
      } finally {
        window.freeGiftAdding = false;
      }
    };

    updateCart();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
